import React, { useState, useEffect } from 'react';
import { 
  X, Plus, Edit2, Trash2, Save, AlertTriangle, Lock, Sparkles,
  Layers, Box, FileText, ListTodo, Calendar, User, Flag, FolderKanban, Info, BarChart3, FolderTree
} from 'lucide-react';
import { api } from '../api/client';
import { useTenant } from '../context/TenantContext';
import { RollupBreakdownPanel, DerivedFieldIndicator, CollapsibleRollupInfo } from './RollupBreakdown';
import { useRollup } from '../hooks/useRollup';
import { WorkItemResourcePanel } from './WorkItemResourcePanel';
import WBSNodeSelector from './WBSNodeSelector';

// Status options
const STATUSES = [
  'Not Started', 'In Progress', 'In Review', 'Blocked', 
  'Behind', 'Due Date at Risk', 'Completed', 'Archived'
];

const PRIORITIES = ['Low', 'Medium', 'High', 'Critical'];

// Type configurations
const TYPE_CONFIG = {
  epic: {
    label: 'Epic',
    icon: Layers,
    color: 'indigo',
    parentField: 'project_id',
    parentLabel: 'Project',
    childType: 'feature'
  },
  feature: {
    label: 'Feature',
    icon: Box,
    color: 'green',
    parentField: 'epic_id',
    parentLabel: 'Epic',
    childType: 'story'
  },
  story: {
    label: 'Story',
    icon: FileText,
    color: 'cyan',
    parentField: 'feature_id',
    parentLabel: 'Feature',
    childType: 'task'
  },
  task: {
    label: 'Task',
    icon: ListTodo,
    color: 'gray',
    parentField: 'parent_story_id',
    parentLabel: 'Story',
    childType: null
  }
};

// Work Item Editor Modal
export const WorkItemEditor = ({ 
  isOpen, 
  onClose, 
  itemType = 'epic', 
  item = null, // null for create, object for edit
  parentId = null,
  parentOptions = [],
  projectId = null,
  tenantId = null,
  onSave,
  onDelete
}) => {
  const { canEdit, isLowerTier, getTier, isStoryLayerEnabled } = useTenant();
  const config = TYPE_CONFIG[itemType];
  const Icon = config?.icon || Layers;
  
  // Rollup hook for derived field detection
  const { 
    rollup, 
    hasChildren, 
    isDerivedField, 
    fetchRollup 
  } = useRollup(itemType, item?.id);
  
  const [showRollupBreakdown, setShowRollupBreakdown] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    status: 'Not Started',
    priority: 'Medium',
    owner: '',
    start_date: '',
    due_date: '',
    hours_estimated: 0,
    hours_actual: 0,
    sprint_id: '',
    release_id: '',
    wbs_id: null,  // WBS Node reference
    // Parent references
    project_id: '',
    epic_id: '',
    feature_id: '',
    story_id: '',
    // Task-specific: parent type selection
    parentType: 'feature' // 'feature' or 'story'
  });
  
  const [sprints, setSprints] = useState([]);
  const [releases, setReleases] = useState([]);
  const [projects, setProjects] = useState([]);
  const [epics, setEpics] = useState([]);
  const [features, setFeatures] = useState([]);
  const [taskParentOptions, setTaskParentOptions] = useState({ features: [], stories: [] });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (item) {
      setFormData({
        name: item.name || '',
        description: item.description || '',
        status: item.status || 'Not Started',
        priority: item.priority || 'Medium',
        owner: item.owner || '',
        start_date: item.start_date || item.planned_start || '',
        due_date: item.due_date || item.planned_end || '',
        hours_estimated: item.hours_estimated || 0,
        hours_actual: item.hours_actual || 0,
        sprint_id: item.sprint_id || '',
        release_id: item.release_id || '',
        wbs_id: item.wbs_id || null,  // WBS Node reference
        project_id: item.project_id || projectId || '',
        epic_id: item.epic_id || '',
        feature_id: item.feature_id || '',
        story_id: item.story_id || '',
        parentType: item.story_id ? 'story' : 'feature'
      });
    } else {
      // Reset form for new item
      setFormData({
        name: '',
        description: '',
        status: 'Not Started',
        priority: 'Medium',
        owner: '',
        start_date: '',
        due_date: '',
        hours_estimated: 0,
        hours_actual: 0,
        sprint_id: '',
        release_id: '',
        wbs_id: null,  // WBS Node reference
        project_id: projectId || parentId || '',
        epic_id: parentId || '',
        feature_id: parentId || '',
        story_id: '',
        parentType: 'feature'
      });
    }
    
    // Load sprints, releases, and parent options
    loadOptions();
  }, [item, isOpen, parentId, projectId, tenantId, itemType]);

  const loadOptions = async () => {
    try {
      const [sprintsRes, releasesRes] = await Promise.all([
        api.getSprints(),
        api.getReleases()
      ]);
      setSprints(sprintsRes.data.sprints || []);
      setReleases(releasesRes.data.releases || []);
      
      // Load projects for epics (to select parent project)
      if (itemType === 'epic') {
        try {
          const projectsRes = await api.getProjects();
          setProjects(projectsRes.data.projects || []);
        } catch (err) {
          console.error('Failed to load projects:', err);
        }
      }
      
      // Load epics for features (to select parent epic)
      if (itemType === 'feature' && projectId) {
        try {
          const epicsRes = await api.getEpics({ project_id: projectId });
          setEpics(epicsRes.data.epics || []);
        } catch (err) {
          console.error('Failed to load epics:', err);
        }
      }
      
      // Load features for stories (to select parent feature)
      if (itemType === 'story' && projectId) {
        try {
          const featuresRes = await api.getFeatures({ project_id: projectId });
          setFeatures(featuresRes.data.features || []);
        } catch (err) {
          console.error('Failed to load features:', err);
        }
      }
      
      // Load parent options for tasks
      if (itemType === 'task' && projectId && tenantId) {
        try {
          const parentRes = await api.getTaskParentOptions(tenantId, projectId);
          setTaskParentOptions({
            features: parentRes.data.features || [],
            stories: parentRes.data.stories || []
          });
        } catch (err) {
          console.error('Failed to load parent options:', err);
        }
      }
    } catch (err) {
      console.error('Failed to load options:', err);
    }
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      setError('Name is required');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const payload = {
        ...formData,
        tenant_id: tenantId
      };

      // Add parent reference based on type
      if (itemType === 'epic') {
        payload.project_id = formData.project_id || parentId || projectId;
      } else if (itemType === 'feature') {
        payload.epic_id = formData.epic_id || parentId;
        payload.project_id = projectId;
      } else if (itemType === 'story') {
        payload.feature_id = formData.feature_id || parentId;
        payload.project_id = projectId;
      } else if (itemType === 'task') {
        // Handle flexible task parenting (XOR: story OR feature)
        if (formData.parentType === 'story' && formData.story_id) {
          payload.story_id = formData.story_id;
          payload.feature_id = null;
        } else {
          payload.feature_id = formData.feature_id || parentId;
          payload.story_id = null;
        }
        payload.project_id = projectId;
        // Remove internal fields
        delete payload.parentType;
      }

      let result;
      if (item) {
        // Update existing
        if (itemType === 'epic') {
          result = await api.updateEpic(item.id, payload);
        } else if (itemType === 'feature') {
          result = await api.updateFeature(item.id, payload);
        } else {
          result = await api.updateWorkItem(item.id, payload);
        }
      } else {
        // Create new
        if (itemType === 'epic') {
          result = await api.createEpic(payload);
        } else if (itemType === 'feature') {
          result = await api.createFeature(payload);
        } else if (itemType === 'story') {
          result = await api.createStory(payload);
        } else if (itemType === 'task') {
          result = await api.createTask(payload);
        }
      }

      if (onSave) onSave(result.data);
      onClose();
    } catch (err) {
      console.error('Save failed:', err);
      setError(err.response?.data?.detail?.message || err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!item) return;
    if (!window.confirm(`Are you sure you want to archive this ${config.label}?`)) return;

    setSaving(true);
    try {
      if (itemType === 'epic') {
        await api.deleteEpic(item.id);
      } else if (itemType === 'feature') {
        await api.deleteFeature(item.id);
      } else {
        await api.deleteWorkItem(item.id);
      }
      if (onDelete) onDelete(item.id);
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to delete');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  // Show upgrade prompt for lower tiers
  if (!canEdit()) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        <div className="fixed inset-0 bg-black/50" onClick={onClose} />
        <div className="relative bg-[var(--clr-surface-card)] rounded-xl shadow-2xl w-full max-w-md mx-4 p-6">
          <div className="text-center">
            <div className="w-16 h-16 bg-[var(--clr-brand-primary)] bg-opacity-10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-[var(--clr-brand-primary)]" />
            </div>
            <h2 className="text-xl font-bold text-[var(--clr-text-primary)] mb-2">Upgrade to Edit</h2>
            <p className="text-[var(--clr-text-secondary)] mb-4">
              Manual editing is available on Business and Enterprise plans. 
              You're currently on the <span className="font-medium capitalize">{getTier()}</span> plan.
            </p>
            <div className="flex gap-3 justify-center">
              <button
                onClick={onClose}
                className="px-4 py-2 border rounded-lg hover:bg-[var(--clr-surface-hover)]"
              >
                Cancel
              </button>
              <button
                onClick={() => window.location.href = '/admin/billing'}
                className="px-4 py-2 bg-[var(--clr-brand-primary)] text-white rounded-lg hover:bg-[var(--clr-brand-primary-hover)] flex items-center gap-2"
              >
                <Sparkles className="w-4 h-4" />
                Upgrade Now
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-[var(--clr-surface-card)] rounded-xl shadow-2xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className={`sticky top-0 bg-${config.color}-50 border-b px-6 py-4 flex items-center justify-between`}>
          <div className="flex items-center gap-3">
            <div className={`p-2 bg-${config.color}-100 rounded-lg`}>
              <Icon className={`w-5 h-5 text-${config.color}-600`} />
            </div>
            <h2 className="text-xl font-bold text-[var(--clr-text-primary)]">
              {item ? `Edit ${config.label}` : `Create ${config.label}`}
            </h2>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-[var(--clr-surface-hover)] rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <div className="p-6 space-y-6">
          {error && (
            <div className="bg-[var(--clr-sev-blocked)] bg-opacity-10 border border-red-200 rounded-lg p-4 flex items-center gap-3 text-red-700">
              <AlertTriangle className="w-5 h-5" />
              <span>{typeof error === 'string' ? error : JSON.stringify(error)}</span>
            </div>
          )}

          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Name <span className="text-[var(--clr-sev-blocked)]">*</span>
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[var(--clr-brand-primary)] focus:border-blue-500"
              placeholder={`Enter ${config.label.toLowerCase()} name`}
              data-testid="workitem-name-input"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              className="w-full px-4 py-2 border rounded-lg h-24 focus:ring-2 focus:ring-[var(--clr-brand-primary)]"
              placeholder="Add a description..."
            />
          </div>

          {/* Derived Fields Notice */}
          {item && itemType !== 'task' && hasChildren && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
              <Lock className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <div className="font-medium text-blue-800 mb-1">Some fields are derived from child tasks</div>
                <p className="text-sm text-blue-700">
                  Status and dates are automatically calculated from child tasks and cannot be manually edited.
                </p>
                <button
                  type="button"
                  onClick={() => setShowRollupBreakdown(true)}
                  className="mt-2 text-sm text-blue-600 hover:text-blue-800 underline flex items-center gap-1"
                >
                  <Info className="w-4 h-4" />
                  See why
                </button>
              </div>
            </div>
          )}

          {/* Status and Priority */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center justify-between">
                <span className="flex items-center gap-1">
                  <Flag className="w-4 h-4" />
                  Status
                  {item && isDerivedField('status') && (
                    <Lock className="w-3 h-3 text-[var(--clr-text-muted)]" title="Derived from child tasks" />
                  )}
                </span>
              </label>
              {item && isDerivedField('status') ? (
                <div 
                  className="w-full px-4 py-2 border rounded-lg bg-gray-100 cursor-not-allowed text-gray-600 flex items-center justify-between"
                  onClick={() => setShowRollupBreakdown(true)}
                >
                  <span>{formData.status}</span>
                  <span className="text-xs text-[var(--clr-text-muted)] flex items-center gap-1">
                    <Lock className="w-3 h-3" /> Derived
                  </span>
                </div>
              ) : (
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({...formData, status: e.target.value})}
                  className="w-full px-4 py-2 border rounded-lg"
                >
                  {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
              <select
                value={formData.priority}
                onChange={(e) => setFormData({...formData, priority: e.target.value})}
                className="w-full px-4 py-2 border rounded-lg"
              >
                {PRIORITIES.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
          </div>

          {/* Owner */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <User className="w-4 h-4 inline mr-1" />
              Owner
            </label>
            <input
              type="text"
              value={formData.owner}
              onChange={(e) => setFormData({...formData, owner: e.target.value})}
              className="w-full px-4 py-2 border rounded-lg"
              placeholder="Owner name"
            />
          </div>

          {/* Dates */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                Start Date
                {item && isDerivedField('start_date') && (
                  <Lock className="w-3 h-3 text-[var(--clr-text-muted)]" title="Derived from child tasks" />
                )}
              </label>
              {item && isDerivedField('start_date') ? (
                <div 
                  className="w-full px-4 py-2 border rounded-lg bg-gray-100 cursor-not-allowed text-gray-600 flex items-center justify-between"
                  onClick={() => setShowRollupBreakdown(true)}
                >
                  <span>{formData.start_date || 'Not set'}</span>
                  <span className="text-xs text-[var(--clr-text-muted)]">MIN</span>
                </div>
              ) : (
                <input
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData({...formData, start_date: e.target.value})}
                  className="w-full px-4 py-2 border rounded-lg"
                />
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                Due Date
                {item && isDerivedField('due_date') && (
                  <Lock className="w-3 h-3 text-[var(--clr-text-muted)]" title="Derived from child tasks" />
                )}
              </label>
              {item && isDerivedField('due_date') ? (
                <div 
                  className="w-full px-4 py-2 border rounded-lg bg-gray-100 cursor-not-allowed text-gray-600 flex items-center justify-between"
                  onClick={() => setShowRollupBreakdown(true)}
                >
                  <span>{formData.due_date || 'Not set'}</span>
                  <span className="text-xs text-[var(--clr-text-muted)]">MAX</span>
                </div>
              ) : (
                <input
                  type="date"
                  value={formData.due_date}
                  onChange={(e) => setFormData({...formData, due_date: e.target.value})}
                  className="w-full px-4 py-2 border rounded-lg"
                />
              )}
            </div>
          </div>

          {/* Hours */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Hours Estimated</label>
              <input
                type="number"
                value={formData.hours_estimated}
                onChange={(e) => setFormData({...formData, hours_estimated: parseFloat(e.target.value) || 0})}
                className="w-full px-4 py-2 border rounded-lg"
                min="0"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Hours Actual</label>
              <input
                type="number"
                value={formData.hours_actual}
                onChange={(e) => setFormData({...formData, hours_actual: parseFloat(e.target.value) || 0})}
                className="w-full px-4 py-2 border rounded-lg"
                min="0"
              />
            </div>
          </div>

          {/* Parent Selection for Epics - Link to Project */}
          {itemType === 'epic' && projects.length > 0 && (
            <div className="bg-[var(--clr-brand-primary)] bg-opacity-10 rounded-lg p-4 border border-blue-200">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <FolderKanban className="w-4 h-4 inline mr-1" />
                Parent Project
              </label>
              <select
                value={formData.project_id}
                onChange={(e) => setFormData({...formData, project_id: e.target.value})}
                className="w-full px-4 py-2 border rounded-lg bg-white"
                data-testid="epic-project-select"
              >
                <option value="">Select a Project...</option>
                {projects.map(project => (
                  <option key={project.id} value={project.id}>{project.name}</option>
                ))}
              </select>
            </div>
          )}

          {/* Parent Selection for Features - Link to Epic */}
          {itemType === 'feature' && epics.length > 0 && (
            <div className="bg-indigo-50 rounded-lg p-4 border border-indigo-200">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Layers className="w-4 h-4 inline mr-1" />
                Parent Epic
              </label>
              <select
                value={formData.epic_id}
                onChange={(e) => setFormData({...formData, epic_id: e.target.value})}
                className="w-full px-4 py-2 border rounded-lg bg-white"
                data-testid="feature-epic-select"
              >
                <option value="">Select an Epic...</option>
                {epics.map(epic => (
                  <option key={epic.id} value={epic.id}>{epic.name}</option>
                ))}
              </select>
            </div>
          )}

          {/* Parent Selection for Stories - Link to Feature */}
          {itemType === 'story' && features.length > 0 && (
            <div className="bg-[var(--clr-sev-healthy)] bg-opacity-10 rounded-lg p-4 border border-green-200">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Box className="w-4 h-4 inline mr-1" />
                Parent Feature
              </label>
              <select
                value={formData.feature_id}
                onChange={(e) => setFormData({...formData, feature_id: e.target.value})}
                className="w-full px-4 py-2 border rounded-lg bg-white"
                data-testid="story-feature-select"
              >
                <option value="">Select a Feature...</option>
                {features.map(feature => (
                  <option key={feature.id} value={feature.id}>{feature.name}</option>
                ))}
              </select>
            </div>
          )}

          {/* Sprint and Release */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Sprint</label>
              <select
                value={formData.sprint_id}
                onChange={(e) => setFormData({...formData, sprint_id: e.target.value})}
                className="w-full px-4 py-2 border rounded-lg"
              >
                <option value="">No Sprint</option>
                {sprints.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Release</label>
              <select
                value={formData.release_id}
                onChange={(e) => setFormData({...formData, release_id: e.target.value})}
                className="w-full px-4 py-2 border rounded-lg"
              >
                <option value="">No Release</option>
                {releases.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
              </select>
            </div>
          </div>

          {/* WBS Node Selector */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <FolderTree className="w-4 h-4 inline mr-1" />
              WBS Location
            </label>
            <WBSNodeSelector
              value={formData.wbs_id}
              onChange={(wbsId) => setFormData({...formData, wbs_id: wbsId})}
              placeholder="Select WBS node"
              allowClear={true}
              data-testid="workitem-wbs-selector"
            />
            <p className="text-xs text-gray-500 mt-1">
              Link this {config.label.toLowerCase()} to a Work Breakdown Structure node
            </p>
          </div>

          {/* Task Parent Selection (only for tasks) */}
          {itemType === 'task' && (
            <div className="bg-[var(--clr-brand-primary)] bg-opacity-10 rounded-lg p-4 border border-blue-200">
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Parent Assignment <span className="text-[var(--clr-sev-blocked)]">*</span>
              </label>
              
              {/* Parent Type Radio */}
              <div className="flex gap-4 mb-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="parentType"
                    value="feature"
                    checked={formData.parentType === 'feature'}
                    onChange={() => setFormData({...formData, parentType: 'feature', story_id: ''})}
                    className="w-4 h-4 text-[var(--clr-brand-primary)]"
                  />
                  <span className="text-sm">Feature (Direct)</span>
                </label>
                
                {isStoryLayerEnabled() && taskParentOptions.stories.length > 0 && (
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="parentType"
                      value="story"
                      checked={formData.parentType === 'story'}
                      onChange={() => setFormData({...formData, parentType: 'story', feature_id: ''})}
                      className="w-4 h-4 text-[var(--clr-brand-primary)]"
                    />
                    <span className="text-sm">Story</span>
                  </label>
                )}
              </div>

              {/* Parent Dropdown */}
              {formData.parentType === 'feature' ? (
                <select
                  value={formData.feature_id}
                  onChange={(e) => setFormData({...formData, feature_id: e.target.value})}
                  className="w-full px-4 py-2 border rounded-lg"
                  required
                >
                  <option value="">Select Feature...</option>
                  {taskParentOptions.features.map(f => (
                    <option key={f.id} value={f.id}>{f.name}</option>
                  ))}
                </select>
              ) : (
                <select
                  value={formData.story_id}
                  onChange={(e) => setFormData({...formData, story_id: e.target.value})}
                  className="w-full px-4 py-2 border rounded-lg"
                  required
                >
                  <option value="">Select Story...</option>
                  {taskParentOptions.stories.map(s => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              )}
              
              {!isStoryLayerEnabled() && (
                <p className="text-xs text-[var(--clr-text-muted)] mt-2">
                  Story layer is disabled. Tasks attach directly to Features.
                </p>
              )}
            </div>
          )}

          {/* P7: Resource Overview Panel - Show only for existing items */}
          {item?.id && (
            <div className="mt-4">
              <WorkItemResourcePanel
                workItemId={item.id}
                tenantId={tenantId}
                editable={canEdit()}
                onNavigate={(type, id) => {
                  // Handle navigation to linked entities
                  console.log('Navigate to:', type, id);
                }}
              />
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-[var(--clr-surface-subtle)] border-t px-6 py-4 flex items-center justify-between">
          {item && (
            <button
              onClick={handleDelete}
              disabled={saving}
              className="px-4 py-2 text-[var(--clr-sev-blocked)] hover:bg-[var(--clr-sev-blocked)] bg-opacity-10 rounded-lg flex items-center gap-2"
            >
              <Trash2 className="w-4 h-4" />
              Archive
            </button>
          )}
          <div className="flex gap-3 ml-auto">
            <button
              onClick={onClose}
              className="px-4 py-2 border rounded-lg hover:bg-[var(--clr-surface-hover)]"
              disabled={saving}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-2 bg-[var(--clr-brand-primary)] text-white rounded-lg hover:bg-[var(--clr-brand-primary-hover)] flex items-center gap-2"
              data-testid="workitem-save-btn"
            >
              {saving ? (
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              {item ? 'Update' : 'Create'} {config.label}
            </button>
          </div>
        </div>
      </div>
      
      {/* Rollup Breakdown Panel */}
      <RollupBreakdownPanel
        entityType={itemType}
        entityId={item?.id}
        entityName={item?.name || formData.name}
        isOpen={showRollupBreakdown}
        onClose={() => setShowRollupBreakdown(false)}
      />
    </div>
  );
};

// Add Button with tier gating
export const AddWorkItemButton = ({ 
  itemType = 'epic', 
  onClick, 
  className = '',
  size = 'md'
}) => {
  const { canEdit } = useTenant();
  const config = TYPE_CONFIG[itemType];
  const Icon = config?.icon || Layers;
  
  const sizeClasses = {
    sm: 'px-3 py-1.5 text-sm',
    md: 'px-4 py-2',
    lg: 'px-5 py-2.5 text-lg'
  };

  if (!canEdit()) {
    return (
      <button
        onClick={onClick}
        className={`flex items-center gap-2 bg-[var(--clr-surface-subtle)] text-[var(--clr-text-muted)] rounded-lg ${sizeClasses[size]} ${className}`}
        title="Upgrade to Business or Enterprise to add items"
      >
        <Lock className="w-4 h-4" />
        Add {config.label}
      </button>
    );
  }

  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-2 bg-[var(--clr-brand-primary)] text-white rounded-lg hover:bg-[var(--clr-brand-primary-hover)] ${sizeClasses[size]} ${className}`}
      data-testid={`add-${itemType}-btn`}
    >
      <Plus className="w-4 h-4" />
      Add {config.label}
    </button>
  );
};

// Status color mapping for inline editing
const STATUS_COLORS = {
  'Blocked': { bg: '#8E44AD', text: 'white' },
  'Behind': { bg: '#E74C3C', text: 'white' },
  'Due Date at Risk': { bg: '#F39C12', text: 'white' },
  'In Progress': { bg: '#3498DB', text: 'white' },
  'In Review': { bg: '#16A085', text: 'white' },
  'Not Started': { bg: '#BDC3C7', text: 'black' },
  'Completed': { bg: '#27AE60', text: 'white' },
  'Archived': { bg: '#7F8C8D', text: 'white' }
};

// Inline Edit Cell for tables
export const InlineEditCell = ({ 
  value, 
  field, 
  type = 'text', 
  options = [],
  itemId,
  itemType,
  onUpdate
}) => {
  const { canEdit } = useTenant();
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (editValue === value) {
      setIsEditing(false);
      return;
    }

    setSaving(true);
    try {
      const payload = { [field]: editValue };
      
      if (itemType === 'epic') {
        await api.updateEpic(itemId, payload);
      } else if (itemType === 'feature') {
        await api.updateFeature(itemId, payload);
      } else {
        await api.updateWorkItem(itemId, payload);
      }
      
      if (onUpdate) onUpdate(itemId, field, editValue);
      setIsEditing(false);
    } catch (err) {
      console.error('Update failed:', err);
      setEditValue(value); // Revert
    } finally {
      setSaving(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleSave();
    if (e.key === 'Escape') {
      setEditValue(value);
      setIsEditing(false);
    }
  };

  if (!canEdit()) {
    // Render non-editable status badge if field is status
    if (field === 'status' && STATUS_COLORS[value]) {
      const colors = STATUS_COLORS[value];
      return (
        <span
          className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
          style={{ backgroundColor: colors.bg, color: colors.text }}
        >
          {value}
        </span>
      );
    }
    return <span>{value || '-'}</span>;
  }

  if (isEditing) {
    if (type === 'select') {
      return (
        <select
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onBlur={handleSave}
          autoFocus
          className="w-full px-2 py-1 border rounded text-sm focus:ring-2 focus:ring-[var(--clr-brand-primary)]"
          data-testid={`inline-edit-${field}`}
        >
          {options.map(opt => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
      );
    }

    return (
      <input
        type={type}
        value={editValue}
        onChange={(e) => setEditValue(e.target.value)}
        onBlur={handleSave}
        onKeyDown={handleKeyDown}
        autoFocus
        className="w-full px-2 py-1 border rounded text-sm focus:ring-2 focus:ring-[var(--clr-brand-primary)]"
        data-testid={`inline-edit-${field}`}
      />
    );
  }

  // Render status badge for status field
  if (field === 'status' && STATUS_COLORS[value]) {
    const colors = STATUS_COLORS[value];
    return (
      <button 
        onClick={() => setIsEditing(true)}
        className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium cursor-pointer hover:ring-2 hover:ring-blue-400 transition-all"
        style={{ backgroundColor: colors.bg, color: colors.text }}
        title="Click to change status"
        data-testid={`status-badge-${itemId}`}
      >
        {value}
        {saving && <span className="ml-1 animate-pulse">...</span>}
      </button>
    );
  }

  // Regular text display with edit hint
  return (
    <span 
      onClick={() => setIsEditing(true)}
      className="cursor-pointer hover:bg-[var(--clr-brand-primary)] bg-opacity-10 px-1 py-0.5 rounded inline-block"
      title="Click to edit"
      data-testid={`editable-${field}-${itemId}`}
    >
      <span className="font-medium text-[var(--clr-text-primary)]">{value || '-'}</span>
      {saving && <span className="ml-1 text-xs text-[var(--clr-text-muted)]">saving...</span>}
    </span>
  );
};

export default WorkItemEditor;
